import { useEffect, useState } from "react";
import { api, fmtAUD } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PreviewBanner from "./PreviewBanner";

// Editorial menu — numbered typographic rows, hover reveals side image
export default function ShopEditorial() {
  const [products, setProducts] = useState([]);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  return (
    <div className="pt-24 pb-24 px-6 md:px-10 min-h-[100svh]" data-testid="preview-shop-editorial">
      <PreviewBanner title="Menu Editorial" kind="Shop / Catalogue" />

      <div className="overline mb-4">Preview / Shop · Editorial</div>
      <h1 className="font-display uppercase text-5xl md:text-8xl tracking-tight leading-[0.9]">The<br />menu.</h1>

      <div className="mt-16 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 md:col-span-8">
          <div className="border-t border-[#1A1A1A]">
            {products.map((p, i) => (
              <a
                key={p.id}
                href={`/product/${p.slug}`}
                onMouseEnter={() => setHover(p)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(p)}
                onBlur={() => setHover(null)}
                data-testid={`menu-row-${p.slug}`}
                className="group block border-b border-[#1A1A1A] py-8 md:py-12 relative"
              >
                <div className="grid grid-cols-12 items-baseline gap-4">
                  <div className="col-span-1 font-mono text-[#444] text-sm">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-7 md:col-span-8">
                    <motion.div
                      className="font-display uppercase text-3xl md:text-6xl tracking-tight leading-[0.95] transition-colors"
                      whileHover={{ x: 12 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {p.name.split("//")[0].trim()}
                      <span className="text-[#444]"> · {p.tier}</span>
                    </motion.div>
                    <div className="overline text-[#666] mt-3">{p.tagline}</div>
                  </div>
                  <div className="col-span-4 md:col-span-3 text-right">
                    <div className="font-display text-xl md:text-3xl">{fmtAUD(p.price_aud)}</div>
                    <div className="overline text-[#444] mt-1">AUD</div>
                  </div>
                </div>
                <ArrowUpRight
                  size={20}
                  className="absolute top-8 right-0 text-[#333] opacity-0 group-hover:opacity-100 group-hover:text-[#EAEAEA] transition-all"
                />
              </a>
            ))}
          </div>
        </div>

        {/* Sticky preview panel */}
        <div className="hidden md:block col-span-4">
          <div className="sticky top-32">
            <div className="border border-[#1A1A1A] aspect-[4/5] overflow-hidden relative bg-[#080808]">
              <AnimatePresence mode="wait">
                {hover ? (
                  <motion.img
                    key={hover.id}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    src={hover.image_url}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover img-clip ${hover.tier === "phantom" ? "phantom" : ""}`}
                  />
                ) : (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center text-[#444] text-sm"
                  >
                    Hover a row to preview
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="mt-6 border border-[#1A1A1A] p-5">
              {hover ? (
                <>
                  <div className="flex items-center justify-between">
                    <div className="overline">{hover.tier}</div>
                    <span className={`tier-dot ${hover.tier}`} />
                  </div>
                  <div className="font-display text-2xl mt-3">{hover.name.split("//")[0]}</div>
                  <div className="overline text-[#666] mt-1">{hover.model}</div>
                  <div className="mt-4 text-[#888] text-xs leading-relaxed">{hover.tagline}</div>
                  <div className="mt-5 font-mono text-lg text-[#EAEAEA]">{fmtAUD(hover.price_aud)}</div>
                </>
              ) : (
                <div className="text-[#444] text-xs uppercase tracking-widest">— Awaiting selection —</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
